/**
 * Name Display Helpers
 *
 * Utilities for formatting client and user names with salutations
 * in various contexts (sorting, display, formal communications)
 */

import { Client } from '@/types';

export type Salutation = '' | 'Dr.' | 'Mr.' | 'Mrs.' | 'Ms.' | 'Miss' | 'Mx.';

/**
 * Get client name formatted for sorting (LastName, FirstName)
 * Used in lists and tables for alphabetical ordering
 *
 * @example
 * getClientSortName({ firstName: 'John', lastName: 'Doe', salutation: 'Dr.' })
 * // Returns: "Doe, John"
 */
export function getClientSortName(client: Pick<Client, 'firstName' | 'lastName'>): string {
  return `${client.lastName}, ${client.firstName}`;
}

/**
 * Get client name formatted for display (Salutation FirstName LastName)
 * Used in UI headings, emails, and general display
 *
 * @example
 * getClientDisplayName({ firstName: 'John', lastName: 'Doe', salutation: 'Dr.' })
 * // Returns: "Dr. John Doe"
 *
 * getClientDisplayName({ firstName: 'Jane', lastName: 'Smith', salutation: '' })
 * // Returns: "Jane Smith"
 */
export function getClientDisplayName(client: Pick<Client, 'firstName' | 'lastName' | 'salutation'>): string {
  const name = `${client.firstName} ${client.lastName}`;
  return client.salutation ? `${client.salutation} ${name}` : name;
}



/**
 * Get client initials for avatar displays (first char of first + last name, uppercase).
 */
export function getClientInitials(client: Pick<Client, 'firstName' | 'lastName'>): string {
  const first = client.firstName?.trim()?.[0] ?? '';
  const last = client.lastName?.trim()?.[0] ?? '';
  return `${first}${last}`.toUpperCase();
}

/**
 * Available salutation options for dropdowns
 */
export const SALUTATION_OPTIONS: { value: Salutation; label: string }[] = [
  { value: '', label: 'None' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'Ms.', label: 'Ms.' },
  { value: 'Miss', label: 'Miss' },
  { value: 'Mx.', label: 'Mx.' },
];
